import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';
import { FormApi } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, Checkbox, FormControlLabel, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { addEllipsis, arrayToStringWithSeparator } from '../../../helpers/commonHelper';
import { RiskLevelSelector } from '../../selectors/RiskLevelSelector';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('RiskAssessedPanel');

// Explicit request (EnquiryFormDrawer split): the "Risk Assessment" accordion.
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface RiskAssessedPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
}

interface RiskAssessedPanelBodyProps extends RiskAssessedPanelProps {
    formValues: AnyObject;
}

const RiskAssessedPanelBody: React.FC<RiskAssessedPanelBodyProps> = React.memo((bodyProps) => {
    const {
        classes,
        formValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            className={classes.expansionPanel}
        // expanded={state.isOtherDetailsPanelExpand}
        // onChange={onExpandPanel('isOtherDetailsPanelExpand')}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="risk-assessed-content"
                id="risk-assessed-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipRed)}>
                        <ReportProblemOutlinedIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelRed, classes.sectionLabelText)}>
                        Risk Assessment
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        <div className="value">
                            {addEllipsis(
                                arrayToStringWithSeparator(
                                    [
                                        values.isRiskAssessed ? 'Yes' : 'No',
                                        values.isRiskAssessed ? values.riskLevelSelector ? values.riskLevelSelector.description : null : null,
                                        values.isRiskAssessed ? values.riskNotes : null
                                    ],
                                    ', '
                                ),
                                70
                            )}
                        </div>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} md={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="isRiskAssessed"
                            type="checkbox"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <FormControlLabel
                                    className={classes.formControlLabel}
                                    control={
                                        <Checkbox
                                            {...input}
                                            checked={values.isRiskAssessed}
                                            onChange={input.onChange}
                                            checkedIcon={<CheckBoxIcon color="primary" />}
                                        />
                                    }
                                    label="Risk Assessed"
                                />
                            )}
                        </Field>
                    </Grid>
                    {values.isRiskAssessed ? (
                        <>
                            <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    // tslint:disable-next-line: max-line-length
                                    name="riskLevelSelector"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <div>
                                            <RiskLevelSelector
                                                variant="outlined"
                                                size="small"
                                                {...input}
                                                label="Risk Level"
                                                name="riskLevelSelector"
                                                className={classes.textField}
                                                error={meta.error && meta.touched}
                                                // tslint:disable-next-line: max-line-length
                                                onSelection={(selection: IAutoCompleteItem, name: string) =>
                                                    input.onChange(selection)
                                                }
                                                helperText={
                                                    meta.error &&
                                                        meta.touched
                                                        ? 'required'
                                                        : ''
                                                }
                                            />
                                        </div>
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="riskNotes"
                                    // validate={required}
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            {...input}
                                            label="Risk Notes"
                                            multiline={true}
                                            // required={true}
                                            className={classes.textField}
                                            error={meta.error && meta.touched}
                                            autoComplete="abcd"
                                            helperText={
                                                meta.error &&
                                                    meta.touched
                                                    ? 'required'
                                                    : ''
                                            }
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </>
                    ) : null}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
RiskAssessedPanelBody.displayName = 'RiskAssessedPanelBody';

export const RiskAssessedPanel: React.FC<RiskAssessedPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <RiskAssessedPanelBody
                {...panelProps}
                formValues={{
                    isRiskAssessed: values.isRiskAssessed,
                    riskLevelSelector: values.riskLevelSelector,
                    riskNotes: values.riskNotes,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
RiskAssessedPanel.displayName = 'RiskAssessedPanel';
